import React, { useState, useEffect, useCallback } from 'react';
import { 
  Eye, 
  Check, 
  X, 
  User, 
  Calendar, 
  Clock, 
  Mail, 
  Phone,
  FileText,
  Users,
  Search,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { PreAppointment, Specialty, Professional, Availability } from '../types';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { formatTime, formatDateArgentine } from '../utils/formatters';
import { motion, AnimatePresence } from 'framer-motion';

interface PreAppointmentsManagerProps {
  onAppointmentUpdate?: () => void;
}

const PreAppointmentsManager: React.FC<PreAppointmentsManagerProps> = ({ onAppointmentUpdate }) => {
  const [preAppointments, setPreAppointments] = useState<PreAppointment[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState<PreAppointment | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [availableProfessionals, setAvailableProfessionals] = useState<Professional[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<Professional | null>(null);
  const [availableSlots, setAvailableSlots] = useState<Availability[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Availability | null>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    specialty_id: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Helper functions for formatting dates and times
  const formatPreferredDate = (dateString: string): string => {
    try {
      return formatDateArgentine(dateString);
    } catch {
      return dateString;
    }
  };

  const formatPreferredTime = (timeString: string): string => {
    try {
      // Handle different time formats
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        return formatTime(date);
      }
      return timeString;
    } catch {
      return timeString;
    }
  };



  const fetchSpecialties = useCallback(async () => {
    try {
      const response = await api.get('/admin/specialties');
      setSpecialties(response.data);
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  }, []);

  const fetchProfessionals = useCallback(async () => {
    try {
      const response = await api.get('/admin/professionals');
      setProfessionals(response.data);
    } catch (error) {
      console.error('Error fetching professionals:', error);
    }
  }, []);

  const fetchPreAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.specialty_id) params.append('specialty_id', filters.specialty_id);
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());

      const response = await api.get(`/admin/pre-appointments?${params.toString()}`);
      setPreAppointments(response.data.pre_appointments);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching pre-appointments:', error);
      toast.error('Error al cargar las solicitudes de turnos');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchSpecialties();
    fetchProfessionals();
  }, [fetchSpecialties, fetchProfessionals]);

  useEffect(() => {
    fetchPreAppointments();
  }, [filters, pagination.page, fetchPreAppointments]);

  const handleStatusUpdate = async (id: number, status: string, adminNotes?: string) => {
    try {
      await api.put(`/admin/pre-appointments/${id}/status`, {
        status,
        admin_notes: adminNotes
      });
      toast.success('Estado actualizado exitosamente');
      await fetchPreAppointments();
      setSelectedAppointment(null);
      onAppointmentUpdate?.();
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar el estado');
    }
  };



  const handleShowAssignmentModal = async (appointment: PreAppointment) => {
    setSelectedAppointment(appointment);
    
    // Get professionals for this specialty
    const specialtyProfessionals = professionals.filter(p => 
      p.specialty === appointment.specialty_id?.toString() || p.specialty === appointment.specialty_name
    );
    
    console.log(`[DEBUG] Appointment specialty:`, appointment.specialty_id, appointment.specialty_name);
    console.log(`[DEBUG] All professionals:`, professionals);
    console.log(`[DEBUG] Filtered professionals for specialty:`, specialtyProfessionals);
    
    setAvailableProfessionals(specialtyProfessionals);
    setShowAssignmentModal(true);
  };

  const handleProfessionalSelection = async (professional: Professional) => {
    setSelectedProfessional(professional);
    setSelectedSlot(null);
    setLoadingAvailability(true);
    
    try {
      console.log(`[DEBUG] Fetching availability for professional:`, professional);
      console.log(`[DEBUG] Professional ID type:`, typeof professional.id, professional.id);
      
      // Convert professional ID to number if it's a string
      const professionalId = typeof professional.id === 'string' ? parseInt(professional.id) : professional.id;
      console.log(`[DEBUG] Converted professional ID:`, professionalId);
      
      // Fetch available slots for this professional
      const response = await api.get(`/admin/professionals/${professionalId}/availability`);
      console.log(`[DEBUG] Availability response:`, response.data);
      setAvailableSlots(response.data || []);
    } catch (error: any) {
      console.error('Error fetching availability:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Error al cargar disponibilidad del profesional');
      setAvailableSlots([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (!selectedAppointment || !selectedProfessional || !selectedSlot) {
      toast.error('Por favor selecciona un profesional y un horario');
      return;
    }

    try {
      // Create the actual appointment
      const appointmentData = {
        professional_id: typeof selectedProfessional.id === 'string' ? parseInt(selectedProfessional.id) : selectedProfessional.id,
        patient_name: selectedAppointment.patient_name,
        patient_email: selectedAppointment.patient_email,
        patient_phone: selectedAppointment.patient_phone,
        appointment_date: selectedSlot.date,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: `Solicitud aprobada desde pre-cita #${selectedAppointment.id}. ${selectedAppointment.admin_notes || ''}`
      };

      const appointmentResponse = await api.post('/admin/appointments', appointmentData);
      
      // Update the pre-appointment to mark it as assigned
      await api.put(`/admin/pre-appointments/${selectedAppointment.id}/assign`, {
        professional_id: typeof selectedProfessional.id === 'string' ? parseInt(selectedProfessional.id) : selectedProfessional.id,
        appointment_id: appointmentResponse.data.id,
        admin_notes: `Turno asignado al Dr. ${selectedProfessional.name || selectedProfessional.user?.name} para ${selectedSlot.date ? formatDateArgentine(selectedSlot.date) : 'fecha no especificada'} a las ${selectedSlot.start_time}`
      });

      toast.success('Turno creado y asignado exitosamente');
      setShowAssignmentModal(false);
      setSelectedAppointment(null);
      setSelectedProfessional(null);
      setSelectedSlot(null);
      await fetchPreAppointments();
      onAppointmentUpdate?.();
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      toast.error(error.response?.data?.error || 'Error al crear el turno');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'pending_assignment': return 'bg-orange-100 text-orange-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'assigned': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'pending_assignment': return 'Esperando Asignación';
      case 'rejected': return 'Rechazado';
      case 'assigned': return 'Asignado';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && preAppointments.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Solicitudes de Turnos</h2>
          <p className="text-gray-600">Revisa y gestiona las solicitudes de turnos de pacientes</p>
        </div>
        <div className="text-sm text-gray-500">
          Total: {pagination.total} solicitudes
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="pending_assignment">Esperando Asignación</option>
              <option value="rejected">Rechazado</option>
              <option value="assigned">Asignado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Especialidad</label>
            <select
              value={filters.specialty_id}
              onChange={(e) => setFilters(prev => ({ ...prev, specialty_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Todas las especialidades</option>
              {specialties.map(specialty => (
                <option key={specialty.id} value={specialty.id}>{specialty.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <div className="relative">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Nombre o email..."
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: '', specialty_id: '', search: '' })}
              className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Pre-appointments List */}
      <div className="space-y-4">
        {preAppointments.length === 0 && !loading && (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
            <h3 className="text-lg font-semibold text-gray-900">No hay solicitudes para mostrar</h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
              Acá aparecen los pedidos de pacientes que no eligieron un horario (cuando una especialidad no tiene horarios publicados).
              Las reservas con horario elegido se aprueban en la pestaña <b>Calendario</b>.
            </p>
          </div>
        )}
        {preAppointments.map((appointment) => (
          <motion.div
            key={appointment.id}
            className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelectedAppointment(appointment)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{appointment.patient_name}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                    {getStatusLabel(appointment.status)}
                  </span>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span>{appointment.patient_email}</span>
                  </div>
                  {appointment.patient_phone && (
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4" />
                      <span>{appointment.patient_phone}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4" />
                    <span>{appointment.specialty_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(appointment.created_at)}</span>
                  </div>
                </div>

                {appointment.preferred_date && (
                  <div className="mt-2 text-sm text-gray-600">
                    <span className="font-medium">Fecha preferida:</span> {formatPreferredDate(appointment.preferred_date)}
                    {appointment.preferred_time && ` a las ${formatPreferredTime(appointment.preferred_time)}`}
                  </div>
                )}
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAppointment(appointment);
                  }}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Ver detalles"
                >
                  <Eye className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page <= 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-600">
            Página {pagination.page} de {pagination.pages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page >= pagination.pages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedAppointment && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Detalles de la Solicitud</h3>
                      <p className="text-sm text-gray-500">Revisa la información del paciente y sus preferencias</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedAppointment(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">

                <div className="space-y-6">
                  {/* Patient Information */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      Información del Paciente
                    </h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <label className="block text-sm font-medium text-gray-600 mb-1">Nombre Completo</label>
                        <p className="text-gray-900 font-medium">{selectedAppointment.patient_name}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                        <p className="text-gray-900 font-medium">{selectedAppointment.patient_email}</p>
                      </div>
                      {selectedAppointment.patient_phone && (
                        <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                          <label className="block text-sm font-medium text-gray-600 mb-1">Teléfono</label>
                          <p className="text-gray-900 font-medium">{selectedAppointment.patient_phone}</p>
                        </div>
                      )}
                      <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <label className="block text-sm font-medium text-gray-600 mb-1">Estado de la Solicitud</label>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedAppointment.status)}`}>
                          {getStatusLabel(selectedAppointment.status)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Appointment Preferences */}
                  {(selectedAppointment.preferred_date || selectedAppointment.preferred_time) && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                          <Calendar className="h-4 w-4 text-white" />
                        </div>
                        Preferencias de Horario
                      </h4>
                      <div className="grid md:grid-cols-2 gap-6">
                        {selectedAppointment.preferred_date && (
                          <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                            <label className="block text-sm font-medium text-gray-600 mb-1">Fecha Preferida</label>
                            <p className="text-gray-900 font-medium">{formatPreferredDate(selectedAppointment.preferred_date)}</p>
                          </div>
                        )}
                        {selectedAppointment.preferred_time && (
                          <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                            <label className="block text-sm font-medium text-gray-600 mb-1">Hora Preferida</label>
                            <p className="text-gray-900 font-medium">{formatPreferredTime(selectedAppointment.preferred_time)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Form Data */}
                  {selectedAppointment.form_data && Object.keys(selectedAppointment.form_data).length > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-blue-600" />
                        Información Específica de {selectedAppointment.specialty_name}
                      </h4>
                      <div className="grid gap-4">
                        {Object.entries(selectedAppointment.form_data).map(([key, value]) => {
                          // Try to find the field configuration to get the proper label
                          const fieldConfig = selectedAppointment.specialty_form_config?.find(field => field.id === key);
                          const fieldLabel = fieldConfig?.label || key.replace(/_/g, ' ').replace(/field\s+\d+/gi, 'Campo personalizado');
                          
                          // Format the value based on field type
                          let displayValue = value;
                          if (Array.isArray(value)) {
                            displayValue = value.join(', ');
                          } else if (fieldConfig?.type === 'checkbox' && typeof value === 'boolean') {
                            displayValue = value ? 'Sí' : 'No';
                          } else if (fieldConfig?.type === 'select' && fieldConfig.options) {
                            // For select fields, try to find the option label
                            const option = fieldConfig.options.find(opt => opt === value || opt.toLowerCase() === String(value).toLowerCase());
                            displayValue = option || String(value);
                          } else {
                            displayValue = String(value);
                          }

                          return (
                            <div key={key} className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                                <div className="flex-1">
                                  <label className="block text-sm font-semibold text-gray-800 mb-1 capitalize">
                                    {fieldLabel}
                                  </label>
                                  <p className="text-gray-700 text-sm leading-relaxed">
                                    {displayValue}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Admin Notes */}
                  {selectedAppointment.admin_notes && (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center mr-3">
                          <FileText className="h-4 w-4 text-white" />
                        </div>
                        Notas del Administrador
                      </h4>
                      <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                        <p className="text-gray-900 leading-relaxed">{selectedAppointment.admin_notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {selectedAppointment.status === 'pending' && (
                    <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mr-3">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                        Acciones Disponibles
                      </h4>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => handleShowAssignmentModal(selectedAppointment)}
                          className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Asignar Profesional
                        </button>
                        <button
                          onClick={() => handleStatusUpdate(selectedAppointment.id, 'rejected')}
                          className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Rechazar Solicitud
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedAppointment.status === 'pending_assignment' && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                          <Users className="h-4 w-4 text-white" />
                        </div>
                        Asignar Profesional
                      </h4>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => handleShowAssignmentModal(selectedAppointment)}
                          className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Asignar Profesional
                        </button>
                        <button
                          onClick={() => handleStatusUpdate(selectedAppointment.id, 'rejected')}
                          className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Rechazar Solicitud
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assignment Modal */}
      <AnimatePresence>
        {showAssignmentModal && selectedAppointment && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Asignar Profesional</h3>
                      <p className="text-sm text-gray-500">Selecciona un profesional y horario para la cita</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAssignmentModal(false);
                      setSelectedAppointment(null);
                      setSelectedProfessional(null);
                      setSelectedSlot(null);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">

                <div className="space-y-6">
                  {/* Patient Info Summary */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      Resumen de la Solicitud
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-sm text-gray-600">Paciente</p>
                        <p className="font-medium text-gray-900">{selectedAppointment.patient_name}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <p className="text-sm text-gray-600">Especialidad</p>
                        <p className="font-medium text-gray-900">{selectedAppointment.specialty_name}</p>
                      </div>
                      {selectedAppointment.preferred_date && (
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-sm text-gray-600">Fecha Preferida</p>
                          <p className="font-medium text-gray-900">{formatPreferredDate(selectedAppointment.preferred_date)}</p>
                        </div>
                      )}
                      {selectedAppointment.preferred_time && (
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-sm text-gray-600">Hora Preferida</p>
                          <p className="font-medium text-gray-900">{formatPreferredTime(selectedAppointment.preferred_time)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Professional Selection */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <Users className="h-4 w-4 text-white" />
                      </div>
                      Seleccionar Profesional
                    </h4>
                    {availableProfessionals.length === 0 ? (
                      <div className="bg-white rounded-lg p-6 border border-gray-100 text-center">
                        <p className="text-gray-500">No hay profesionales disponibles para esta especialidad.</p>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {availableProfessionals.map((professional) => (
                          <div
                            key={professional.id}
                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                              selectedProfessional?.id === professional.id
                                ? 'border-blue-500 bg-blue-50 shadow-md'
                                : 'border-gray-200 hover:border-blue-300 hover:shadow-sm bg-white'
                            }`}
                            onClick={() => handleProfessionalSelection(professional)}
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  selectedProfessional?.id === professional.id
                                    ? 'bg-blue-500'
                                    : 'bg-gray-200'
                                }`}>
                                  <User className={`h-5 w-5 ${
                                    selectedProfessional?.id === professional.id
                                      ? 'text-white'
                                      : 'text-gray-600'
                                  }`} />
                                </div>
                                <div>
                                  <h5 className="font-medium text-gray-900">{professional.name || professional.user?.name}</h5>
                                  <p className="text-sm text-gray-600">{professional.email || professional.user?.email}</p>
                                </div>
                              </div>
                              {selectedProfessional?.id === professional.id && (
                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                  <Check className="h-4 w-4 text-white" />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Available Time Slots */}
                  {selectedProfessional && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                          <Calendar className="h-4 w-4 text-white" />
                        </div>
                        Horarios Disponibles - Dr. {selectedProfessional.name || selectedProfessional.user?.name}
                      </h4>
                      {loadingAvailability ? (
                        <div className="bg-white rounded-lg p-6 border border-gray-100 text-center">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                          </div>
                          <p className="text-gray-700 font-medium">Cargando disponibilidad...</p>
                          <p className="text-sm text-gray-500">Buscando horarios disponibles</p>
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <div className="bg-white rounded-lg p-6 border border-gray-100 text-center">
                          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="h-6 w-6 text-yellow-600" />
                          </div>
                          <p className="text-gray-700 font-medium mb-2">No hay horarios disponibles</p>
                          <p className="text-sm text-gray-500">
                            Este profesional no tiene disponibilidad configurada o todos los horarios están ocupados.
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            Contacta al profesional para que configure su disponibilidad.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          {/* Progress Indicator */}
                          <div className="flex items-center justify-center space-x-4 mb-6">
                            <div className={`flex items-center space-x-2 ${selectedSlot?.date ? 'text-purple-600' : 'text-gray-400'}`}>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedSlot?.date ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                <Calendar className="h-4 w-4" />
                              </div>
                              <span className="text-sm font-medium">1. Fecha</span>
                            </div>
                            <div className={`w-8 h-0.5 ${selectedSlot?.date ? 'bg-purple-500' : 'bg-gray-200'}`}></div>
                            <div className={`flex items-center space-x-2 ${selectedSlot?.start_time ? 'text-purple-600' : 'text-gray-400'}`}>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedSlot?.start_time ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                <Clock className="h-4 w-4" />
                              </div>
                              <span className="text-sm font-medium">2. Horario</span>
                            </div>
                          </div>

                          {/* Step 1: Select Date */}
                          <div>
                            <h5 className="text-md font-medium text-gray-800 mb-3 flex items-center">
                              <Calendar className="h-4 w-4 mr-2 text-purple-600" />
                              Selecciona una fecha
                            </h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {Array.from(new Set(availableSlots.map(slot => slot.date))).map((date) => (
                                <div
                                  key={date}
                                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                    selectedSlot?.date === date
                                      ? 'border-purple-500 bg-purple-50 shadow-md'
                                      : 'border-gray-200 hover:border-purple-300 hover:shadow-sm bg-white'
                                  }`}
                                  onClick={() => {
                                    // Clear time selection when date changes
                                    setSelectedSlot(null);
                                    // Set the date but keep time empty
                                    const firstSlotForDate = availableSlots.find(slot => slot.date === date);
                                    if (firstSlotForDate) {
                                      setSelectedSlot({ ...firstSlotForDate, start_time: '', end_time: '' });
                                    }
                                  }}
                                >
                                  <div className="text-center">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2 ${
                                      selectedSlot?.date === date
                                        ? 'bg-purple-500'
                                        : 'bg-gray-200'
                                    }`}>
                                      <Calendar className={`h-4 w-4 ${
                                        selectedSlot?.date === date
                                          ? 'text-white'
                                          : 'text-gray-600'
                                      }`} />
                                    </div>
                                    <p className="font-medium text-gray-900 text-sm">{formatDateArgentine(date || '')}</p>
                                    <p className="text-xs text-gray-600">
                                      {availableSlots.filter(slot => slot.date === date).length} horarios
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Step 2: Select Time (only if date is selected) */}
                          {selectedSlot?.date && (
                            <div>
                              <h5 className="text-md font-medium text-gray-800 mb-3 flex items-center">
                                <Clock className="h-4 w-4 mr-2 text-purple-600" />
                                Selecciona un horario
                              </h5>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {availableSlots
                                  .filter(slot => slot.date === selectedSlot.date)
                                  .map((slot) => (
                                    <div
                                      key={`${slot.date}-${slot.start_time}`}
                                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                        selectedSlot?.start_time === slot.start_time
                                          ? 'border-purple-500 bg-purple-50 shadow-md'
                                          : 'border-gray-200 hover:border-purple-300 hover:shadow-sm bg-white'
                                      }`}
                                      onClick={() => setSelectedSlot(slot)}
                                    >
                                      <div className="text-center">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2 ${
                                          selectedSlot?.start_time === slot.start_time
                                            ? 'bg-purple-500'
                                            : 'bg-gray-200'
                                        }`}>
                                          <Clock className={`h-4 w-4 ${
                                            selectedSlot?.start_time === slot.start_time
                                              ? 'text-white'
                                              : 'text-gray-600'
                                          }`} />
                                        </div>
                                        <p className="font-medium text-gray-900 text-sm">
                                          {slot.start_time} - {slot.end_time}
                                        </p>
                                        <p className="text-xs text-gray-600">
                                          {slot.duration} min
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Appointment Summary */}
                  {selectedProfessional && selectedSlot?.date && selectedSlot?.start_time && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                        Resumen del Turno
                      </h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-sm text-gray-600">Profesional</p>
                          <p className="font-medium text-gray-900">{selectedProfessional.name || selectedProfessional.user?.name}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-sm text-gray-600">Fecha</p>
                          <p className="font-medium text-gray-900">{selectedSlot.date ? formatDateArgentine(selectedSlot.date) : 'No especificada'}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-100">
                          <p className="text-sm text-gray-600">Horario</p>
                          <p className="font-medium text-gray-900">{selectedSlot.start_time} - {selectedSlot.end_time}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200">
                    <div className="flex flex-col sm:flex-row justify-end gap-3">
                      <button
                        onClick={() => {
                          setShowAssignmentModal(false);
                          setSelectedAppointment(null);
                          setSelectedProfessional(null);
                          setSelectedSlot(null);
                        }}
                        className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleCreateAppointment}
                        disabled={!selectedProfessional || !selectedSlot || !selectedSlot.start_time}
                        className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Crear Turno
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PreAppointmentsManager;
